import _ from 'lodash';
import React from 'react';
import {
    ClaimResponsibilityType,
    ProviderClaimResponsibilityCategoryType,
    SupportClaimResponsibilityCategoryType,
} from '../../api';
import { normalizeStr } from '../../services';
import { Emoji } from '../base/emoji/emoji';
import { Tooltip } from '../base/tooltip';
import { Badge } from './badge';
import { BadgeType } from './badge.styles';
import * as S from './responsibility-badge.styles';
import { BadgeSize } from './transmission-status-badge';

export { getResponsibilityColors } from './responsibility-badge.styles';

export interface IResponsibilityBadgeProps {
    className?: string;
    size: BadgeSize;
    responsibility?: keyof typeof ClaimResponsibilityType;
}

export class ResponsibilityBadge extends React.Component<IResponsibilityBadgeProps> {
    public render(): React.ReactElement {
        const { size, responsibility, className } = this.props;
        const label = responsibility
            ? _.startCase(ClaimResponsibilityType[responsibility]?.toLowerCase())
            : ClaimResponsibilityType.UNKNOWN;

        return (
            <S.Badge size={size} responsibility={responsibility} className={className} title="Responsibility">
                <S.BadgeLabel>{label}</S.BadgeLabel> {size === 'large' && this.renderBadgeIcon()}
            </S.Badge>
        );
    }
    private readonly renderBadgeIcon = (): React.ReactElement | string => {
        if (this.props.responsibility) {
            switch (this.props.responsibility) {
                case ClaimResponsibilityType.ENTER:
                    return <Emoji type="lightning" />;
                case ClaimResponsibilityType.GUARANTOR:
                    return <Emoji type="faceWithThermometer" />;
                case ClaimResponsibilityType.NONE:
                    return <Emoji type="thumbsUp" />;
                case ClaimResponsibilityType.PAYER:
                    return <Emoji type="hospital" />;
                case ClaimResponsibilityType.PROVIDER:
                    return <Emoji type="healthWorker" />;
                case ClaimResponsibilityType.UNKNOWN:
                    return <Emoji type="faceWithMonocle" />;
                default:
                    return <Emoji type="faceWithMonocle" />;
            }
        }

        return <Emoji type="faceWithMonocle" />;
    };
}

export function getResponsibilityDescription(type: ClaimResponsibilityType) {
    const dict = {
        [ClaimResponsibilityType.ENTER]: "This claim is on Enter's to-do list.",
        [ClaimResponsibilityType.PAYER]: "We're waiting on the Payer.",
        [ClaimResponsibilityType.PROVIDER]: "We're waiting on the Provider's response.",
        [ClaimResponsibilityType.GUARANTOR]: "We're waiting on the Guarantor to pay.",
        [ClaimResponsibilityType.NONE]: 'This claim is done! 🎉',
        [ClaimResponsibilityType.UNKNOWN]: "Something went wrong. We're looking into it.",
    };

    return dict[type];
}

export function getResponsibilityCategoryDescription(
    type: SupportClaimResponsibilityCategoryType | ProviderClaimResponsibilityCategoryType,
) {
    const dict: Record<SupportClaimResponsibilityCategoryType, string> = {
        [SupportClaimResponsibilityCategoryType.CLAIMCLOSEOUT]: 'This claim is closed.',
        [SupportClaimResponsibilityCategoryType.CLAIMPAID]: 'The claim was fully paid.',
        [SupportClaimResponsibilityCategoryType.FAXRECEIVED]: 'We received a fax.',
        [SupportClaimResponsibilityCategoryType.MAILRECEIVED]: 'We received a letter.',
        [SupportClaimResponsibilityCategoryType.PHONERECEIVED]: 'We received a phone call.',
        [SupportClaimResponsibilityCategoryType.PAYMENTRECEIVED]: 'We received a payment.',
        [SupportClaimResponsibilityCategoryType.HL7RECEIVED]: 'We received an HL7 message. ',
        [SupportClaimResponsibilityCategoryType.PROFESSIONALCREATED]: 'The professional claim (CMS1500) was created.',
        [SupportClaimResponsibilityCategoryType.TRANSMISSIONWITHIN45DAYS]:
            'This claim was sent to the Payer in the last 45 days. ',
        [SupportClaimResponsibilityCategoryType.TRANSMISSIONBEYOND45DAYS]:
            'This claim has been with the Payer for over 45 days.',
        [SupportClaimResponsibilityCategoryType.TRANSMISSIONREJECTEDORDENIED]:
            'This claim was rejected or denied by the Payer.',
        [SupportClaimResponsibilityCategoryType.FAXFAILED]: 'The fax attempt failed. ',
        [SupportClaimResponsibilityCategoryType.LETTERPAYERADDRESSNOTVALID]:
            "The letter did not send because the Payer's address was not valid.",
        [SupportClaimResponsibilityCategoryType.LETTERPAYERWITHIN45DAYS]:
            'The appeal (mail) was sent to the Payer in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.LETTERPAYERBEYOND45DAYS]:
            'This appeal (mail) has been with the Payer for over 45 days.',
        [SupportClaimResponsibilityCategoryType.LETTERGUARANTORWITHIN45DAYS]:
            'The bill was sent to the Patient in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.LETTERGUARANTORBEYOND45DAYS]:
            'This bill has been with the Patient for over 45 days.',
        [SupportClaimResponsibilityCategoryType.FAXPAYERNUMBERNOTVALID]:
            "The fax did not send because the Payer's fax number was not valid.",
        [SupportClaimResponsibilityCategoryType.FAXWITHIN45DAYS]:
            'The appeal (fax) was sent to the Payer in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.FAXBEYOND45DAYS]:
            'This appeal (fax) has been with the Payer for over 45 days.',
        [SupportClaimResponsibilityCategoryType.PHONEWITHIN45DAYS]:
            'The appeal (phone) was made to the Payer in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.PHONEBEYOND45DAYS]:
            'This appeal (phone) has been with the Payer for over 45 days.',
        [SupportClaimResponsibilityCategoryType.SUPPORTCOMMENTWITHIN7DAYS]:
            'This support ticket has been addressed in the last 7 days.',
        [SupportClaimResponsibilityCategoryType.SUPPORTCOMMENTBEYOND7DAYS]:
            'This support ticket has been outstanding for over 7 days.',
        [SupportClaimResponsibilityCategoryType.SUPPORTCOMMENT]:
            'This support ticket is pending a response.',
        [SupportClaimResponsibilityCategoryType.PROVIDERCOMMENT]:
            'This provider initiated support ticket is pending a response.',
        [SupportClaimResponsibilityCategoryType.CLAIMEMPTY]: 'This claim has no usable information in it at this time.',
        [SupportClaimResponsibilityCategoryType.CROSSOVERTRANSMISSIONWITHIN45DAYS]:
            'This claim was transmitted to the next payer by the previous payer in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.CROSSOVERTRANSMISSIONBEYOND45DAYS]:
            'This claim was transmitted to the next payer by the previous payer over 45 days ago with no response.',
        [SupportClaimResponsibilityCategoryType.PAYERRESPONSIBILITYTAGWITHIN45DAYS]:
            'This claim responsibility was changed to provider responsibility by system tag in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.PAYERRESPONSIBILITYTAGBEYOND45DAYS]:
            'This claim responsibility was changed to provider responsibility by system tag over 45 days ago.',
        [SupportClaimResponsibilityCategoryType.GUARANTOROVERPAYMENT]:
            'There is a guarantor overpayment on this claim and a refund needs to be issued and recorded.',
        [SupportClaimResponsibilityCategoryType.INTERNALESCALATION]:
            'This claim has been escalated within Enter for further review and processing.',
        [SupportClaimResponsibilityCategoryType.GUARANTORCOLLECTIONS]:
            'This claim is in guarantor collections.',
        [SupportClaimResponsibilityCategoryType.PROVIDERRESPONSIBILITYTAGWITHIN45DAYS]:
            'This claim responsibility was changed by system tag in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.PROVIDERRESPONSIBILITYTAGBEYOND45DAYS]:
            'This claim responsibility was changed by system tag over 45 days ago.',
        [SupportClaimResponsibilityCategoryType.CODINGWITHIN7DAYS]:
            'This claim was coded in the last 7 days.',
        [SupportClaimResponsibilityCategoryType.CODINGBEYOND7DAYS]:
            'This claim has been awaiting coding for over 7 days.',
        [SupportClaimResponsibilityCategoryType.INACTIVECHARGEMASTERITEM]:
            'This claim has an inactive chargemaster item.',
        [SupportClaimResponsibilityCategoryType.INSTITUTIONALCREATED]:
            'The institutional claim (CMS-1450 / UB04) was created.',
        [SupportClaimResponsibilityCategoryType.JSONRECEIVED]: 'We received a JSON claim update.',
        [SupportClaimResponsibilityCategoryType.CODINGSUBMITTED]: 'Coding was submitted for this claim.',
        [SupportClaimResponsibilityCategoryType.CODINGCOMPLETED]: 'Coding was completed for this claim.',
        [SupportClaimResponsibilityCategoryType.CODINGERROR]: 'Coding encountered an error on this claim.',
        [SupportClaimResponsibilityCategoryType.CLAIMUPDATERECEIVED]: 'We received a claim update.',
        [SupportClaimResponsibilityCategoryType.EMAILFAILED]: 'The email attempt failed. ',
        [SupportClaimResponsibilityCategoryType.EMAILPAYERNOTVALID]:
            "The email did not send because the Payer's email was not valid.",
        [SupportClaimResponsibilityCategoryType.EMAILWITHIN45DAYS]:
            'The appeal (email) was sent to the Payer in the last 45 days.',
        [SupportClaimResponsibilityCategoryType.EMAILBEYOND45DAYS]:
            'This appeal (email) has been with the Payer for over 45 days.',
        [SupportClaimResponsibilityCategoryType.CLAIMSTATUSRECEIVED]: 'We received a claim status update.',
        [SupportClaimResponsibilityCategoryType.CLAIMHALT]: 'This claim has been halted.',
        [SupportClaimResponsibilityCategoryType.CONSECUTIVEWORKERERRORS]:
            'The claim processing worker has encountered consecutive errors.',
        [SupportClaimResponsibilityCategoryType.CONSECUTIVEWORKERFAILURES]:
            'The claim processing worker has failed repeatedly.',
        [SupportClaimResponsibilityCategoryType.UNKNOWN]: 'The responsibility category for this claim is unknown.',
    };
    return dict[type as SupportClaimResponsibilityCategoryType];
}

export const ClaimResponsibilityCategoryBadge: React.FC<{
    category: SupportClaimResponsibilityCategoryType | ProviderClaimResponsibilityCategoryType;
}> = ({ category }) => {
    const description = getResponsibilityCategoryDescription(category) ?? normalizeStr(category);

    return (
        <Tooltip direction="top" variant="dark" title={<span>{description}</span>}>
            <Badge type={BadgeType.BLUE_LIGHT} size="small" title={description}>
                {normalizeStr(category)}
            </Badge>
        </Tooltip>
    );
};
